import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowRight, ArrowLeft, Loader2, UserX, MapPin, Mail, Phone,
  UtensilsCrossed, TrendingUp, Sparkles, Camera, MessageSquare,
  Users, Dumbbell, BadgeCheck, Shield, Star, LayoutDashboard, Pencil,
  Link2, Check, Share2, Eye, CreditCard, Package,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import { getApiBase } from "@/lib/api";
import { isMobile, isStandalone } from "@/lib/deviceDetect";

const BACKEND_URL = getApiBase();

function getDashboardPath(role) {
  if (role === "admin") return "/admin/dashboard";
  if (role === "coach" || role === "student") return `/${role}/dashboard`;
  return "/dashboard";
}

const SERVICE_META = {
  diet: { label: "Plans nutrition", desc: "Objectifs macro adaptés à chaque profil", icon: UtensilsCrossed },
  fitness: { label: "Programmes training", desc: "Séances structurées et évolutives", icon: Dumbbell },
  stats: { label: "Suivi de progression", desc: "Mesures et graphiques de performance", icon: TrendingUp },
  ai: { label: "Assistant IA", desc: "Recommandations intelligentes en continu", icon: Sparkles },
  photos: { label: "Photos de suivi", desc: "Visualisation des transformations", icon: Camera },
  messaging: { label: "Messagerie directe", desc: "Échanges sécurisés avec votre coach", icon: MessageSquare },
};

const SKILL_LABELS = {
  skill_fitness: "Coaching sportif",
  skill_nutrition: "Nutrition",
  skill_ia: "Outils IA",
};

function shouldUseSameTabNavigation() {
  return isMobile() || isStandalone();
}

function formatProfileViewsSince(isoDate) {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return isoDate;
  return `${day}/${month}/${year}`;
}

function pickRandomStudents(students, count = 5) {
  if (!students?.length) return [];
  const pool = [...students];
  for (let i = pool.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, Math.min(count, pool.length));
}

function FacebookIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function XIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function LinkedInIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 114.126 0 2.063 2.063 0 01-2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function WhatsAppIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function ProfileShareBar({ coach, pseudo }) {
  const [linkCopied, setLinkCopied] = useState(false);
  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;
  const useSameTab = shouldUseSameTabNavigation();

  const shareUrl = `${typeof window !== "undefined" ? window.location.origin : "https://www.mycoaching.be"}/@${pseudo}`;
  const shareTitle = `${coach.name} — Coach sur MyCoaching`;
  const shareText = coach.punch_line
    ? `Découvrez ${coach.name} : ${coach.punch_line}`
    : `Découvrez le profil de ${coach.name} sur MyCoaching`;

  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(`${shareText} ${shareUrl}`);

  const shareLinks = [
    {
      id: "facebook",
      label: "Facebook",
      className: "bg-[#1877F2] hover:bg-[#166FE5] text-white",
      icon: FacebookIcon,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      id: "x",
      label: "X",
      className: "bg-slate-900 hover:bg-slate-800 text-white",
      icon: XIcon,
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodeURIComponent(shareText)}`,
    },
    {
      id: "linkedin",
      label: "LinkedIn",
      className: "bg-[#0A66C2] hover:bg-[#0958A8] text-white",
      icon: LinkedInIcon,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    },
    {
      id: "whatsapp",
      label: "WhatsApp",
      className: "bg-[#25D366] hover:bg-[#20BD5A] text-white",
      icon: WhatsAppIcon,
      href: `https://api.whatsapp.com/send?text=${encodedText}`,
    },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      toast.success("Lien copié dans le presse-papiers");
      setTimeout(() => setLinkCopied(false), 2500);
    } catch {
      toast.error("Impossible de copier le lien");
    }
  };

  const nativeShare = async () => {
    try {
      await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Partage annulé");
    }
  };

  const shareButtonClass = "inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors shadow-sm";

  return (
    <div className="mt-8 pt-6 border-t border-slate-200/80">
      <p className="text-sm font-medium text-slate-500 mb-3 text-center lg:text-left">
        Partager ce profil
      </p>
      <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
        {canNativeShare && (
          <button
            type="button"
            onClick={nativeShare}
            className={`${shareButtonClass} border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700 ${useSameTab ? "order-first w-full sm:w-auto justify-center" : ""}`}
          >
            <Share2 className="w-4 h-4" />
            {useSameTab ? "Partager via l'appareil" : "Partager…"}
          </button>
        )}
        {shareLinks.map((item) => (
          <a
            key={item.id}
            href={item.href}
            target={useSameTab ? undefined : "_blank"}
            rel="noopener noreferrer"
            className={`${shareButtonClass} ${item.className}`}
            aria-label={`Partager sur ${item.label}`}
          >
            <item.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{item.label}</span>
          </a>
        ))}
        <button
          type="button"
          onClick={copyLink}
          className={`${shareButtonClass} border border-slate-200 bg-white hover:bg-slate-50 text-slate-700`}
        >
          {linkCopied ? <Check className="w-4 h-4 text-green-600" /> : <Link2 className="w-4 h-4" />}
          {linkCopied ? "Copié !" : "Copier le lien"}
        </button>
      </div>
    </div>
  );
}

export default function PublicCoachProfile() {
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [reviews, setReviews] = useState({ reviews: [], avg_rating: 0, total: 0 });
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [checkoutForm, setCheckoutForm] = useState({ name: "", email: "" });
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const rawSlug = params.pseudo || params.slug || '';
  const pseudo = rawSlug.startsWith('@') ? rawSlug.substring(1) : rawSlug;

  useEffect(() => {
    if (!pseudo) {
      navigate('/', { replace: true });
      return;
    }

    const fetchProfile = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/profile/public/${pseudo}`);
        setCoach(data);
        if (data?.user_id) {
          try {
            const revRes = await axios.get(`${BACKEND_URL}/api/reviews/coach/${data.user_id}`);
            setReviews(revRes.data);
          } catch {}
        }
        setPackagesLoading(true);
        try {
          const pkgRes = await axios.get(`${BACKEND_URL}/api/profile/public/${pseudo}/packages`);
          setPackages(pkgRes.data?.packages || []);
        } catch {
          setPackages([]);
        } finally {
          setPackagesLoading(false);
        }
      } catch (err) {
        if (err.response?.status === 404) setNotFound(true);
        else navigate('/', { replace: true });
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [pseudo, navigate]);

  const services = useMemo(() => {
    const raw = coach?.visible_services?.split(',').map(s => s.trim()).filter(Boolean) ?? [];
    return raw.map(id => ({ id, ...SERVICE_META[id] })).filter(s => s.label);
  }, [coach?.visible_services]);

  const displayedStudents = useMemo(
    () => pickRandomStudents(coach?.student_photos, 5),
    [coach?.student_photos]
  );

  const specialties = useMemo(() => {
    if (!coach?.specialties?.trim()) return [];
    return coach.specialties.split(',').map(s => s.trim()).filter(Boolean);
  }, [coach?.specialties]);

  const skills = useMemo(() => {
    if (!coach) return [];
    return Object.entries(SKILL_LABELS)
      .map(([key, label]) => ({ label, value: coach[key] ?? 0 }))
      .filter(s => s.value > 0);
  }, [coach]);

  const initials = coach?.name
    ? coach.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  const joinUrl = coach ? `/login?coach_code=${coach.coach_code}` : '/login';

  const openCheckout = (pkg) => {
    setSelectedPackage(pkg);
    setCheckoutForm({
      name: user?.name || "",
      email: user?.email || "",
    });
    setCheckoutOpen(true);
  };

  const handlePublicCheckout = async (e) => {
    e.preventDefault();
    if (!selectedPackage || !checkoutForm.email.trim() || !checkoutForm.name.trim()) return;
    setCheckoutLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/payments/public/checkout`, {
        email: checkoutForm.email.trim(),
        name: checkoutForm.name.trim(),
        package_id: selectedPackage.package_id,
        coach_pseudo: pseudo,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de lancer le paiement");
      setCheckoutLoading(false);
    }
  };
  const firstName = coach?.name?.split(' ')[0] ?? 'ce coach';
  const dashboardPath = user ? getDashboardPath(user.role) : null;
  const isOwnProfile = user?.role === "coach" && coach?.user_id === user.user_id;

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 flex flex-col items-center justify-center gap-4 px-6">
        <UserX className="w-14 h-14 text-blue-200" />
        <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Profil introuvable</h1>
        <p className="text-slate-500 text-center">Ce coach n'existe pas ou n'est plus disponible.</p>
        <Button onClick={() => navigate('/')} className="rounded-full bg-blue-600 hover:bg-blue-700 text-white mt-2">
          Retour à l'accueil
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {user && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(dashboardPath)}
                className="shrink-0 text-slate-600 hover:text-blue-600"
                aria-label="Retour au dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            <button
              onClick={() => navigate(user ? dashboardPath : "/")}
              className="font-heading text-xl sm:text-2xl font-bold tracking-tight uppercase text-slate-900 truncate"
            >
              COACH.
            </button>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {user ? (
              <>
                {isOwnProfile && (
                  <Button
                    onClick={() => navigate("/coach/profile")}
                    variant="outline"
                    className="hidden sm:inline-flex rounded-full border-slate-200"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Modifier
                  </Button>
                )}
                <Button
                  onClick={() => navigate(dashboardPath)}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-4 sm:px-6 shadow-md shadow-blue-500/20"
                >
                  <LayoutDashboard className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">
                    {user.role === "coach" ? "Mon espace coach" : "Mon compte"}
                  </span>
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => navigate("/login")}
                  variant="ghost"
                  className="text-slate-600 hover:text-slate-900 hidden sm:inline-flex"
                >
                  Connexion
                </Button>
                <Button
                  onClick={() => navigate(joinUrl)}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-4 sm:px-6 shadow-md shadow-blue-500/20"
                >
                  Rejoindre
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-28 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-blue-100/40" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-blue-200/30 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-blue-100/50 to-transparent rounded-full blur-3xl translate-y-1/3 -translate-x-1/4 pointer-events-none" />

        <div className="relative max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-[auto_1fr] gap-10 lg:gap-14 items-center">
            {/* Photo */}
            <div className="flex justify-center lg:justify-start">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-br from-blue-400 to-blue-600 rounded-3xl opacity-20 blur-sm" />
                {coach.picture ? (
                  <img
                    src={coach.picture}
                    alt={coach.name}
                    className="relative w-44 h-44 sm:w-52 sm:h-52 rounded-3xl object-cover ring-4 ring-white shadow-xl shadow-blue-900/10"
                  />
                ) : (
                  <div className="relative w-44 h-44 sm:w-52 sm:h-52 rounded-3xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center ring-4 ring-white shadow-xl shadow-blue-900/10">
                    <span className="text-5xl font-heading font-bold text-white">{initials}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Infos */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium mb-5">
                <BadgeCheck className="w-4 h-4" />
                Coach certifié MyCoaching
              </div>

              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-slate-900 leading-[0.95]">
                {coach.name}
              </h1>
              <p className="mt-2 text-blue-600/70 font-medium">@{coach.pseudo}</p>

              {coach.punch_line && (
                <p className="mt-5 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                  {coach.punch_line}
                </p>
              )}

              <div className="mt-7 flex flex-wrap gap-3 justify-center lg:justify-start">
                {coach.location && (
                  <span className="inline-flex items-center gap-2 text-sm text-slate-600 bg-white/80 backdrop-blur px-4 py-2 rounded-full border border-slate-200/80 shadow-sm">
                    <MapPin className="w-4 h-4 text-blue-500" />
                    {coach.location}
                  </span>
                )}
                {coach.student_count != null && (
                  <span className="inline-flex items-center gap-2 text-sm text-slate-600 bg-white/80 backdrop-blur px-4 py-2 rounded-full border border-slate-200/80 shadow-sm">
                    <Users className="w-4 h-4 text-blue-500" />
                    {coach.student_count} élève{coach.student_count > 1 ? 's' : ''}
                  </span>
                )}
                {reviews.total > 0 && (
                  <span className="inline-flex items-center gap-2 text-sm text-slate-600 bg-white/80 backdrop-blur px-4 py-2 rounded-full border border-slate-200/80 shadow-sm">
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    {reviews.avg_rating}/5 ({reviews.total} avis)
                  </span>
                )}
              </div>

              {specialties.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-2 justify-center lg:justify-start">
                  {specialties.map((tag, i) => (
                    <span
                      key={i}
                      className="text-xs font-semibold px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <Button
                  onClick={() => navigate(joinUrl)}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 h-12 text-base shadow-lg shadow-blue-500/25"
                >
                  Commencer avec {firstName}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                {(coach.email || coach.phone) && (
                  <Button
                    variant="outline"
                    onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                    className="rounded-full px-8 h-12 border-slate-200 hover:bg-white"
                  >
                    Contacter
                  </Button>
                )}
              </div>

              <ProfileShareBar coach={coach} pseudo={pseudo} />

              {displayedStudents.length > 0 && (
                <div className="mt-8 flex items-center gap-3 justify-center lg:justify-start">
                  <div className="flex -space-x-2">
                    {displayedStudents.map((s, i) => (
                      s.picture ? (
                        <img key={i} src={s.picture} alt="" className="w-9 h-9 rounded-full object-cover ring-2 ring-white shadow-sm" />
                      ) : (
                        <div key={i} className="w-9 h-9 rounded-full bg-blue-100 ring-2 ring-white flex items-center justify-center text-xs font-bold text-blue-700">
                          {s.name?.[0]?.toUpperCase()}
                        </div>
                      )
                    ))}
                  </div>
                  <span className="text-sm text-slate-500">Communauté active</span>
                </div>
              )}

              {coach.profile_views_since && (
                <p className="mt-4 flex items-center gap-2 text-sm text-slate-400 justify-center lg:justify-start">
                  <Eye className="w-4 h-4 shrink-0" />
                  Cette page a été consultée{" "}
                  <span className="font-medium text-slate-500">
                    {Number(coach.profile_view_count).toLocaleString("fr-BE")}
                  </span>{" "}
                  fois depuis le {formatProfileViewsSince(coach.profile_views_since)}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Contenu */}
      <div className="max-w-6xl mx-auto px-6 pb-20 space-y-16">

        {/* Services */}
        {services.length > 0 && (
          <section>
            <div className="text-center mb-10">
              <p className="text-sm font-medium text-blue-600 uppercase tracking-widest mb-2">Accompagnement</p>
              <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
                Un coaching complet
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {services.map(svc => (
                <Card key={svc.id} className="border-slate-100 shadow-sm hover:shadow-md hover:border-blue-100 transition-all bg-white">
                  <CardContent className="p-6">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center mb-4">
                      <svc.icon className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="font-semibold text-slate-900">{svc.label}</h3>
                    <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{svc.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Forfaits */}
        {(packagesLoading || packages.length > 0) && (
          <section>
            <div className="text-center mb-10">
              <p className="text-sm font-medium text-blue-600 uppercase tracking-widest mb-2">Tarifs</p>
              <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
                Forfaits disponibles
              </h2>
              <p className="text-slate-500 mt-2 text-sm max-w-md mx-auto">
                Payez en ligne sans créer de compte — vous recevrez un accès après paiement.
              </p>
            </div>
            {packagesLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {packages.map((pkg) => (
                  <Card key={pkg.package_id} className="border-slate-100 shadow-sm hover:shadow-md hover:border-blue-100 transition-all bg-white flex flex-col">
                    <CardContent className="p-6 flex flex-col flex-1">
                      <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center mb-4">
                        <Package className="w-5 h-5 text-emerald-600" />
                      </div>
                      <h3 className="font-semibold text-slate-900 text-lg">{pkg.name}</h3>
                      {pkg.description && (
                        <p className="text-sm text-slate-500 mt-2 leading-relaxed flex-1">{pkg.description}</p>
                      )}
                      <div className="mt-5 pt-4 border-t border-slate-100">
                        <p className="text-3xl font-bold text-slate-900">
                          {Number(pkg.amount).toFixed(0)}
                          <span className="text-base font-medium text-slate-400 ml-1">€</span>
                        </p>
                        {pkg.duration && (
                          <p className="text-xs text-slate-400 mt-1">{pkg.duration}</p>
                        )}
                        <Button
                          onClick={() => openCheckout(pkg)}
                          className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-2"
                        >
                          <CreditCard className="w-4 h-4" />
                          Payer maintenant
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </section>
        )}

        <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {selectedPackage ? `Acheter — ${selectedPackage.name}` : "Paiement"}
              </DialogTitle>
            </DialogHeader>
            {selectedPackage && (
              <form onSubmit={handlePublicCheckout} className="space-y-4 mt-2">
                <p className="text-sm text-slate-500">
                  Entrez vos coordonnées pour régler <strong>{selectedPackage.amount} €</strong> et rejoindre {coach.name}.
                </p>
                <div>
                  <Label htmlFor="checkout-name">Nom complet</Label>
                  <Input
                    id="checkout-name"
                    value={checkoutForm.name}
                    onChange={(e) => setCheckoutForm((f) => ({ ...f, name: e.target.value }))}
                    className="mt-1.5 h-11"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="checkout-email">Email</Label>
                  <Input
                    id="checkout-email"
                    type="email"
                    value={checkoutForm.email}
                    onChange={(e) => setCheckoutForm((f) => ({ ...f, email: e.target.value }))}
                    className="mt-1.5 h-11"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={checkoutLoading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-11 gap-2"
                >
                  {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  {checkoutLoading ? "Redirection..." : "Continuer vers Stripe"}
                </Button>
                <p className="text-xs text-slate-400 text-center">
                  Après paiement, connectez-vous avec cet email pour accéder à votre espace élève.
                </p>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Bio + Skills */}
        {(coach.bio || skills.length > 0) && (
          <section className="grid lg:grid-cols-3 gap-8">
            {coach.bio && (
              <div className={skills.length > 0 ? 'lg:col-span-2' : 'lg:col-span-3'}>
                <Card className="border-slate-100 shadow-sm h-full bg-gradient-to-br from-white to-blue-50/30">
                  <CardContent className="p-8 sm:p-10">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                        <Shield className="w-5 h-5 text-white" />
                      </div>
                      <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-slate-900">
                        À propos
                      </h2>
                    </div>
                    <div className="text-slate-600 leading-[1.8] whitespace-pre-line">
                      {coach.bio}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
            {skills.length > 0 && (
              <div className={coach.bio ? '' : 'lg:col-span-3 max-w-md mx-auto w-full'}>
                <Card className="border-slate-100 shadow-sm h-full">
                  <CardContent className="p-8">
                    <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-slate-900 mb-6">
                      Expertise
                    </h3>
                    <div className="space-y-5">
                      {skills.map(s => (
                        <div key={s.label}>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="font-medium text-slate-700">{s.label}</span>
                            <span className="text-blue-600 font-semibold">{s.value}%</span>
                          </div>
                          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-600"
                              style={{ width: `${s.value}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </section>
        )}

        {/* Reviews */}
        {reviews.reviews?.length > 0 && (
          <section>
            <div className="text-center mb-10">
              <p className="text-sm font-medium text-blue-600 uppercase tracking-widest mb-2">Témoignages</p>
              <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
                Avis des élèves
              </h2>
              {reviews.total > 0 && (
                <div className="flex items-center justify-center gap-1 mt-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className={`w-5 h-5 ${i < Math.round(reviews.avg_rating) ? "fill-yellow-400 text-yellow-400" : "text-slate-200"}`} />
                  ))}
                  <span className="text-sm text-slate-500 ml-2">{reviews.avg_rating}/5 · {reviews.total} avis</span>
                </div>
              )}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {reviews.reviews.map(r => (
                <Card key={r.review_id} className="border-slate-100 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      {r.student_picture ? (
                        <img src={r.student_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-sm font-bold text-blue-700">
                          {r.student_name?.[0]?.toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{r.student_name}</p>
                        <div className="flex gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "fill-yellow-400 text-yellow-400" : "text-slate-200"}`} />
                          ))}
                        </div>
                      </div>
                    </div>
                    {r.comment && <p className="text-sm text-slate-600 leading-relaxed">"{r.comment}"</p>}
                    <p className="text-xs text-slate-400 mt-2">
                      {new Date(r.created_at).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Contact */}
        {(coach.email || coach.phone) && (
          <section id="contact">
            <Card className="border-slate-100 shadow-sm">
              <CardContent className="p-8 sm:p-10">
                <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-slate-900 mb-6">
                  Contact
                </h2>
                <div className="flex flex-wrap gap-4">
                  {coach.email && (
                    <a
                      href={`mailto:${coach.email}`}
                      className="inline-flex items-center gap-3 px-5 py-3.5 bg-blue-50 hover:bg-blue-100 rounded-xl text-sm text-slate-700 transition-colors border border-blue-100"
                    >
                      <Mail className="w-4 h-4 text-blue-600" />
                      {coach.email}
                    </a>
                  )}
                  {coach.phone && (
                    <a
                      href={`tel:${coach.phone}`}
                      className="inline-flex items-center gap-3 px-5 py-3.5 bg-blue-50 hover:bg-blue-100 rounded-xl text-sm text-slate-700 transition-colors border border-blue-100"
                    >
                      <Phone className="w-4 h-4 text-blue-600" />
                      {coach.phone}
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {/* CTA */}
        <section className="relative rounded-3xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(255,255,255,0.15)_0%,_transparent_60%)]" />
          <div className="relative px-8 sm:px-16 py-14 sm:py-20 text-center text-white">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight">
              Prêt à transformer votre parcours ?
            </h2>
            <p className="mt-4 text-blue-100 text-lg max-w-lg mx-auto">
              Rejoignez {coach.name} sur MyCoaching et accédez à un suivi professionnel personnalisé.
            </p>
            <Button
              onClick={() => navigate(joinUrl)}
              className="mt-8 bg-white text-blue-700 hover:bg-blue-50 rounded-full px-10 h-12 text-base font-semibold shadow-xl"
            >
              Rejoindre ce coach
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="mt-5 text-sm text-blue-200/80">
              Code coach : <span className="font-mono font-medium text-white">{coach.coach_code}</span>
            </p>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-100 bg-gradient-to-b from-white to-blue-50/50 py-8 px-6 text-center">
        <p className="text-sm text-slate-400">
          Propulsé par{" "}
          <button
            onClick={() => navigate('/')}
            className="font-heading font-bold uppercase text-blue-600 hover:text-blue-700 transition-colors"
          >
            COACH.
          </button>
        </p>
      </footer>
    </div>
  );
}